import React, { useState } from 'react';
import { Badge } from '@/ui/badge';
import { Button } from '@/ui/Button';
import { Switch } from '@/ui/switch';
import { Edit, Trash2, Eye, EyeOff, Copy, Plus, Check } from 'lucide-react';
import { type CustomRule } from '@/shared/services/supabaseService';
import RepositorySelector from '@/features/rules/components/RepositorySelector';
import { toast } from 'sonner';

interface RuleCardProps {
  rule: CustomRule;
  onEdit?: (rule: CustomRule) => void;
  onDelete?: (ruleId: string) => void;
  onTogglePublic?: (ruleId: string, isPublic: boolean) => void;
  onCopy?: (rule: CustomRule) => void;
  onUseRule?: (rule: CustomRule, repositoryIds: string[]) => void;
  showActions?: boolean;
  showUsage?: boolean;
  isInRepository?: boolean;
  isEnabled?: boolean;
  onToggleEnabled?: (ruleId: string, enabled: boolean) => void;
  showUseButton?: boolean;
}

const RuleCard: React.FC<RuleCardProps> = ({
  rule,
  onEdit,
  onDelete,
  onTogglePublic,
  onCopy,
  onUseRule,
  showActions = true,
  showUsage = true,
  isInRepository = false,
  isEnabled = true,
  onToggleEnabled,
  showUseButton = false
}) => {
  const [showRepositorySelector, setShowRepositorySelector] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleEdit = () => {
    if (onEdit) onEdit(rule);
  };

  const handleDelete = () => {
    if (onDelete) onDelete(rule.id);
  };

  const handleTogglePublic = () => {
    if (onTogglePublic) onTogglePublic(rule.id, !rule.is_public);
  };

  const handleToggleEnabled = () => {
    if (onToggleEnabled) onToggleEnabled(rule.id, !isEnabled);
  };

  const handleUseRule = (repositoryIds: string[]) => {
    if (onUseRule) onUseRule(rule, repositoryIds);
  };

  const handleCopyContent = async () => {
    try {
      await navigator.clipboard.writeText(rule.rule_content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy content:', error);
      toast.error('Failed to copy content');
    }
  };

  return (
    <div className="group w-full h-full flex flex-col border border-neutral-950 dark:border-neutral-100 bg-background hover:bg-neutral-50 dark:hover:bg-neutral-900/20 transition-colors">
      <div className="p-4 border-b border-neutral-950 dark:border-neutral-100 bg-neutral-50 dark:bg-neutral-900/10 flex items-start justify-between">
          <div className="flex-1 min-w-0 pr-4">
            <div className="flex items-center gap-2 mb-1">
                <h3 className="text-sm font-mono font-bold uppercase tracking-wide truncate">
                    {rule.title}
                </h3>
                {rule.is_public && (
                    <Badge variant="secondary" className="rounded-none text-[10px] h-5 px-1.5 font-mono uppercase border border-neutral-950 dark:border-neutral-100 bg-white dark:bg-black text-foreground">
                        Public
                    </Badge>
                )}
            </div>
            {rule.description && (
              <p className="text-xs text-muted-foreground font-mono truncate">
                {rule.description}
              </p>
            )}
          </div>
          {isInRepository && onToggleEnabled && (
            <Switch
              checked={isEnabled}
              onCheckedChange={handleToggleEnabled}
              className="data-[state=checked]:bg-green-600 dark:data-[state=checked]:bg-green-500 border-neutral-950 dark:border-neutral-100"
            />
          )}
      </div>

      <div className="flex-1 p-4 flex flex-col gap-4">
          {/* Rule Content Preview */}
          <div className="flex-1 bg-neutral-100 dark:bg-neutral-900 p-3 border border-neutral-200 dark:border-neutral-800 font-mono text-xs text-muted-foreground whitespace-pre-wrap max-h-[150px] overflow-y-auto">
              {rule.rule_content}
          </div>

          <div className="flex items-center justify-between border-t border-neutral-200 dark:border-neutral-800 pt-3">
             <div className="text-[10px] font-mono text-muted-foreground uppercase">
                {showUsage && (
                    <span>USED_BY: {rule.usage_count} REPOS</span>
                )}
             </div>

             <div className="flex items-center gap-1">
                 {/* Use Button */}
                 {showUseButton && onUseRule && !showActions && (
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowRepositorySelector(true)}
                        className="h-7 px-2 rounded-none hover:bg-neutral-200 dark:hover:bg-neutral-800 font-mono text-xs uppercase"
                    >
                        <Plus className="h-3 w-3 mr-1" />
                        USE_RULE
                    </Button>
                 )}

                 {/* Copy Button */}
                 {(onCopy || (!showActions && showUseButton)) && (
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleCopyContent}
                        className="h-7 px-2 rounded-none hover:bg-neutral-200 dark:hover:bg-neutral-800 font-mono text-xs uppercase"
                    >
                        {copied ? <Check className="h-3 w-3 text-green-600" /> : <Copy className="h-3 w-3" />}
                    </Button>
                 )}

                 {/* Edit/Delete Actions */}
                 {showActions && (
                    <>
                        {onTogglePublic && (
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={handleTogglePublic}
                                className="h-7 w-7 rounded-none hover:bg-neutral-200 dark:hover:bg-neutral-800"
                                title={rule.is_public ? "Make Private" : "Make Public"}
                            >
                                {rule.is_public ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                            </Button>
                        )}
                        {onEdit && (
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={handleEdit}
                                className="h-7 w-7 rounded-none hover:bg-neutral-200 dark:hover:bg-neutral-800"
                                title="Edit Rule"
                            >
                                <Edit className="h-3 w-3" />
                            </Button>
                        )}
                        {onDelete && (
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={handleDelete}
                                className="h-7 w-7 rounded-none hover:bg-red-100 dark:hover:bg-red-900/30 text-red-600 dark:text-red-400"
                                title="Delete Rule"
                            >
                                <Trash2 className="h-3 w-3" />
                            </Button>
                        )}
                    </>
                 )}
             </div>
          </div>
      </div>

      {/* Repository Selector Dialog */}
      {showUseButton && onUseRule && (
        <RepositorySelector
          isOpen={showRepositorySelector}
          onClose={() => setShowRepositorySelector(false)}
          onApply={handleUseRule}
          ruleTitle={rule.title}
        />
      )}
    </div>
  );
};

export default RuleCard;
